import React, { useEffect, useRef } from 'react';
import Layout from '@shared/ui/organisms/layout/Layout';
import styled from "styled-components";
import { HeadlineMedium } from '@shared/ui/atoms/fonts/Headline';
import { GoBackButton } from '@shared/ui/atoms/buttons/GoBackButton';
import { useParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { selectAllOfficeEntities } from "@features/desks/store/advanceHotDeskingSetup.duck";
import { findOfficeInOffices, getOfficeChildren } from "@shared/utils/OfficeHelper";
import { IconTypes } from '@shared/ui/atoms/icon/Icon';
import { OfficeInformationPanel } from "./components/OfficeInformationPanel";
import { OfficeInformationView } from "./data/OfficeManagement.model";
import { DeskBookingPanel } from "./components/DeskBookingPanel";
import { SidebarItem } from "./components/SidebarItem";
import { getParameterByName, setQueryParams } from "@shared/utils/UrlUtils";
import { OfficeEntity } from '@features/desks/services/AdvanceHotDeskingService';
import {
  resetEditOfficeLayout,
  selectActivePanel,
  selectEditingOffice,
  setActivePanel,
  setEditingOffice
} from "@features/desks/store/editOfficeLayout.duck";
import { CarParkBookingPanel } from "./components/CarParkBookingPanel";
import { MeetingRoomsPanel } from "./components/MeetingRoomsPanel";
import { tablet } from '@shared/ui/atoms/MediaQueries';
import Colours from '@shared/ui/atoms/Colours';
import { HospitalityPanel } from "./components/HospitalityPanel";
import { AutomaticCheckinPanel } from "./components/AutomaticCheckinPanel";
import { selectCurrentUser } from "@shared/store/auth.duck";

export function OfficeManagementPageV2(props: Props) {
  const params: any = useParams();
  const officeId = params.officeId;
  const panelId = params.panelId;
  const dispatch = useDispatch();
  const allOffices = useSelector(selectAllOfficeEntities);
  const editingOffice = useSelector(selectEditingOffice);
  const activePanel = useSelector(selectActivePanel);
  const currentUser = useSelector(selectCurrentUser);

  useEffect(() => {
    const qsPanel = getParameterByName('panel', window.location.href);
    if (qsPanel) {
      dispatch(setActivePanel(parseInt(qsPanel)))
    }
    return () => {
      dispatch(resetEditOfficeLayout());
    }
  }, [dispatch, panelId]);

  useEffect(() => {
    if (officeId) {
      const currentOffice = findOfficeInOffices(allOffices, parseInt(officeId));
      if (currentOffice) {
        const officeId = parseInt(params.officeId)
        const office = allOffices.find((office: OfficeEntity) => office.id === officeId);
        if (office) {
          const officeChildren = getOfficeChildren(office.id, allOffices);
          dispatch(setEditingOffice({office: office, children: officeChildren}))
        }
      }
    }
  }, [dispatch, params, allOffices, officeId]);

  const initialLoad = useRef(true);
  useEffect(() => {
    if (initialLoad.current) {
      initialLoad.current = false;
      return;
    }
    setQueryParams([`panel=${activePanel}`]);
  }, [activePanel]);

  const onPanelChange = (panel: OfficeInformationView) => {
    dispatch(setActivePanel(panel));
  }

  return (
    <>
      <Layout fullWidth={true} hideFooter={true}>
        <RibbonContainer>
          <RibbonContentContainer>
            <GoBackButton url={'/manage/offices'} />
            <HeadlineMedium>{editingOffice?.label ?? 'New'}</HeadlineMedium>
          </RibbonContentContainer>
        </RibbonContainer>

        <ContentContainer>
          <Sidebar>
            <SidebarItem icon={IconTypes.Office}
                         label={'settings.office-information'}
                         active={activePanel === OfficeInformationView.OfficeInformation}
                         onClick={() => onPanelChange(OfficeInformationView.OfficeInformation)} />
            {editingOffice && <>
              <SidebarItem icon={IconTypes.Chair}
                           label={'settings.desk-booking'}
                           active={activePanel === OfficeInformationView.DeskBooking}
                           onClick={() => onPanelChange(OfficeInformationView.DeskBooking)} />
              <SidebarItem icon={IconTypes.Office}
                           label={'whereabouts.car-park-bookings'}
                           active={activePanel === OfficeInformationView.CarParkBooking}
                           onClick={() => onPanelChange(OfficeInformationView.CarParkBooking)} />
              <SidebarItem icon={IconTypes.Office}
                           label={'settings-page.meeting-rooms'}
                           active={activePanel === OfficeInformationView.MeetingRooms}
                           onClick={() => onPanelChange(OfficeInformationView.MeetingRooms)} />
              <SidebarItem icon={IconTypes.Users}
                           label={'header.hospitality'}
                           active={activePanel === OfficeInformationView.Hospitality}
                           onClick={() => onPanelChange(OfficeInformationView.Hospitality)} />
              {currentUser?.companyEntity.automaticOfficeCheckinEnabled &&
                <SidebarItem icon={IconTypes.Office}
                             label={'settings.automatic-checkin'}
                             active={activePanel === OfficeInformationView.AutomaticCheckin}
                             onClick={() => onPanelChange(OfficeInformationView.AutomaticCheckin)} />}
            </>
          }
          </Sidebar>
          <MainContent>
            {activePanel === OfficeInformationView.OfficeInformation && <OfficeInformationPanel office={editingOffice} />}
            {editingOffice && <>
              {activePanel === OfficeInformationView.DeskBooking && <DeskBookingPanel office={editingOffice} />}
              {activePanel === OfficeInformationView.CarParkBooking && <CarParkBookingPanel office={editingOffice} />}
              {activePanel === OfficeInformationView.MeetingRooms && <MeetingRoomsPanel office={editingOffice} />}
              {activePanel === OfficeInformationView.Hospitality && <HospitalityPanel office={editingOffice} />}
              {activePanel === OfficeInformationView.AutomaticCheckin && <AutomaticCheckinPanel office={editingOffice} />}
            </>}
          </MainContent>
        </ContentContainer>
      </Layout>
    </>
  )
}

interface Props {
}

const Sidebar = styled.div`
  width: 220px;
  min-width: 220px;
  max-width: 220px;
  margin: 0 auto 0 auto;
  padding-top: 36px;
  padding-bottom: 12px;
  @media (${tablet}) {
    margin: 0;
    padding-top: 0;
    padding-bottom: 0;
  }
`

const MainContent = styled.div`
  flex: 1;
  min-height: 500px;
  background-color: white;
  padding: 32px 16px 0 16px;
  @media (${tablet}) {
    padding: 48px 120px;
  }
`

const ContentContainer = styled.div`
  max-width: 1400px;
  background-color: ${Colours.veryLightGrey};
  @media (${tablet}) {
    padding: 0 16px;
    background-color: unset;
    display: flex;
    margin: 24px auto 0 auto;
  }
`

const RibbonContainer = styled.div`
  background-color: white;
  padding: 0 16px;
  box-shadow: 0 9px 22px 0 rgba(46, 74, 148, 0.08);
`

const RibbonContentContainer = styled.div`
  width: 100%;
  margin: 0 auto;
  max-width: 1400px;
  padding: 16px 0;
  @media (${tablet}) {
    padding: 16px 0;
  }
`
